/* @flow */

import type { ViewMode } from "shop-state/types";

import React, { useContext } from "react";
import { NavHashLink } from "react-router-hash-link";
import { Link, NavLink, useLocation } from "react-router-dom";
import { QuoteData } from "data";
import { useData } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import CustomerBalance from "components/CustomerBalance";
import Wrapper from "components/Wrapper";
import Button from "components/Button";
import Logo from "../logo.svg";
import CartIcon from "icons/cart.svg";
import CloseIcon from "icons/cross.svg";
import CustomerServiceIcon from "icons/customer-service.svg";
import SearchIcon from "icons/search.svg";

import cn from "classnames";
import styles from "../Small/styles.scss";

type Props = {
  hamburgerOpen: boolean,
  offCanvasNode: any,
  viewMode: string,
  subNavOpen: boolean,
  setViewMode: (ViewMode) => void,
  setSubNavOpen: (boolean) => void,
  setHamburgerOpen: (boolean) => void,
  closeHamburger: () => void,
};

const MobileMenu = ({
  hamburgerOpen,
  offCanvasNode,
  setViewMode,
  viewMode,
  setSubNavOpen,
  subNavOpen,
  setHamburgerOpen,
  closeHamburger,
}: Props): React$Node => {
  const quoteData = useData(QuoteData);
  const location = useLocation();
  const t = useTranslate();
  const { occasions } = useContext(StoreInfoContext);
  const occasionCategories =
    occasions && occasions.category.children ? occasions.category.children : [];

  const activeRedeem = quoteData.data && quoteData.data.retain24validation;

  const setModeAndOpenMenu = (mode: ViewMode) => {
    setViewMode(mode);
    setSubNavOpen(true);
  };

  const setModeAndCloseHamburger = (mode: ViewMode) => {
    setViewMode(mode);
    setHamburgerOpen(false);
  };

  return (
    <nav
      ref={offCanvasNode}
      id="mobileMenu"
      aria-label={t("HEADER.MAIN_NAVIGATION")}
      aria-hidden={!hamburgerOpen ?? "true"}
      className={styles.drawer}
    >
      <Wrapper>
        <header className={styles.top}>
          <Logo
            alt={t("LOGO.ALT")}
            aria-label={t("LOGO.TITLE")}
            className={styles.logo}
          />

          <Button aria-label={t("HEADER.CLOSE_MENU")} onClick={closeHamburger}>
            <CloseIcon aria-label={t("ICONS.CROSS")} />
          </Button>
        </header>

        <div className={styles.importantItems}>
          {!activeRedeem &&
            <NavLink
              className={styles.item}
              activeClassName={styles.active}
              to="/zupergift"
            >
              {t("HEADER.BUY_ZUPERGIFT")}
            </NavLink>
          }

          {!activeRedeem &&
            <NavLink
              className={styles.item}
              activeClassName={styles.active}
              to="/redeem"
            >
              {t("HEADER.REDEEM_GIFTCARD")}
            </NavLink>
          }

          <NavLink
            className={styles.item}
            activeClassName={styles.active}
            to={t("ROUTES.ALL_GIFTCARDS")}
          >
            {t("HEADER.ALL_GIFTCARDS")}
          </NavLink>

          <Button
            className={styles.item}
            aria-controls="categoriesMobile"
            aria-expanded={(viewMode === "categories" && subNavOpen) ?? "true"}
            onClick={() => setModeAndOpenMenu("categories")}
          >
            {t("HEADER.CATEGORIES")}
          </Button>

          {occasionCategories.length > 0 &&
            <Button
              className={styles.item}
              aria-controls="occasionsMobile"
              aria-expanded={viewMode === "occasions"}
              onClick={() => setModeAndOpenMenu("occasions")}
            >
              {t("HEADER.OCCASIONS")}
            </Button>
          }

          <NavHashLink
            smooth
            className={cn(
              styles.item,
              { [styles.active]: process.browser && (location.pathname + location.hash === "/#about") }
            )}
            to="/#about"
            location={{ pathname: location.pathname + location.hash }}
            onClick={() => setModeAndCloseHamburger("normal")}
          >
            {t("HEADER.ABOUT")}
          </NavHashLink>

          <NavLink
            className={styles.item}
            activeClassName={styles.active}
            to="/company"
          >
            {t("HEADER.COMPANY")}
          </NavLink>

        </div>

        <NavLink
          className={styles.item}
          activeClassName={styles.active}
          to="/faq"
        >&nbsp;<CustomerServiceIcon aria-label={t("ICONS.HEADPHONES")} />
          <span>{t("HEADER.HELP")}</span>
        </NavLink>

        <Button
          className={styles.item}
          onClick={() => setModeAndCloseHamburger("search")}
        >&nbsp;<SearchIcon aria-label={t("ICONS.MAGNIFIER")} />
          <span>{t("HEADER.SEARCH")}</span>
        </Button>

        <Link
          className={styles.item}
          aria-label={t("HEADER.CART")}
          to={t("URL.CHECKOUT_CART")}
        >&nbsp;<CartIcon aria-label={t("ICONS.CART")} />
          <span>{t("HEADER.CART")}</span>
        </Link>

      </Wrapper>
      <Wrapper className={styles.bottom}>
        <CustomerBalance />
      </Wrapper>
    </nav>
  );
};

export default MobileMenu;
